// type-tester.js — Vanilla JS TypeTester class
// Replaces all jQuery-based type.js logic from the old site.
// One class handles all 8 fonts generically via JSON config.

(function () {
  'use strict';

  function TypeTester(containerEl) {
    this.container = containerEl;
    this.fontName = containerEl.dataset.font;

    try {
      // Handle HTML-escaped JSON from Liquid
      var raw = containerEl.dataset.config.replace(/&quot;/g, '"').replace(/&#39;/g, "'");
      this.config = JSON.parse(raw);
    } catch (e) {
      console.error('TypeTester: invalid config JSON', e);
      return;
    }

    var defaultSize = window.innerWidth <= 768 ? 40 : (this.config.defaultSize || 120);

    this.state = {
      axes: Object.assign({}, this.config.defaultAxes),
      fontSize: defaultSize,
      leading: 0.9,
      tracking: 0,
      alignment: 'left',
      uppercase: false,
      features: {}
    };

    // Init feature states from config defaults
    if (this.config.features) {
      this.config.features.forEach(function (f) {
        this.state.features[f.tag] = !!f.default;
      }, this);
    }

    this.textarea = containerEl.querySelector('[data-tester-text]');
    this.controlsEl = containerEl.querySelector('[data-tester-controls]');

    if (!this.textarea || !this.controlsEl) return;

    this._buildControls();
    this._bind();
    this._render();
  }

  // Build axis sliders and feature buttons from config JSON (avoids relying on Liquid parse_json)
  TypeTester.prototype._buildControls = function () {
    var self = this;
    // Placeholder used as insertion point; axis controls go directly into the
    // flex row so they sit inline with Size / Leading / Tracking. We avoid
    // display:contents which is buggy with dynamically appended children in WebKit.
    var axesPlaceholder = this.controlsEl.querySelector('[data-axes-container]');
    var innerEl = this.controlsEl.querySelector('.tester-controls__inner');
    var insertParent = (axesPlaceholder && axesPlaceholder.parentNode) || innerEl;

    // --- Axis sliders: insert directly into the flex row ---
    if (this.config.axes) {
      var axisKeys = Object.keys(this.config.axes);
      axisKeys.forEach(function (key) {
        var ax = self.config.axes[key];
        var defaultVal = self.config.defaultAxes[key] !== undefined
          ? self.config.defaultAxes[key]
          : ax.min;
        var step = parseFloat(ax.step || 1);

        var group = document.createElement('div');
        group.className = 'tester-control';
        group.setAttribute('data-axis-group', '');

        var label = document.createElement('label');
        label.className = 'tester-control__label';

        var labelText = document.createTextNode(ax.label || key);
        label.appendChild(labelText);

        var valueSpan = document.createElement('span');
        valueSpan.setAttribute('data-axis-value', '');
        valueSpan.textContent = step >= 1 ? Math.round(defaultVal) : parseFloat(defaultVal).toFixed(2);
        label.appendChild(valueSpan);

        var input = document.createElement('input');
        input.type = 'range';
        input.setAttribute('data-axis', key);
        input.min = ax.min;
        input.max = ax.max;
        input.step = ax.step || 1;
        input.value = defaultVal;
        input.setAttribute('aria-label', ax.label || key);

        group.appendChild(label);
        group.appendChild(input);
        // Insert before placeholder so axes appear first in the row
        insertParent.insertBefore(group, axesPlaceholder);
      });
      // Remove the now-empty placeholder
      if (axesPlaceholder) {
        insertParent.removeChild(axesPlaceholder);
      }
    }

  };

  TypeTester.prototype._bind = function () {
    var self = this;

    // Clear placeholder on focus; restore it if user leaves the field empty
    this.textarea.addEventListener('focus', function () {
      if (self.textarea.classList.contains('is-default')) {
        self.textarea.textContent = '';
        self.textarea.classList.remove('is-default');
      }
    });

    this.textarea.addEventListener('blur', function () {
      if (self.textarea.textContent.trim() === '') {
        self.textarea.textContent = 'type something...';
        self.textarea.classList.add('is-default');
      }
    });

    // Axis sliders
    this.controlsEl.querySelectorAll('[data-axis]').forEach(function (input) {
      input.addEventListener('input', function () {
        var axis = input.dataset.axis;
        self.state.axes[axis] = parseFloat(input.value);
        self._updateValueDisplay(input);
        self._render();
      });
    });

    // Size slider
    var sizeInput = this.controlsEl.querySelector('[data-control="size"]');
    if (sizeInput) {
      sizeInput.addEventListener('input', function () {
        self.state.fontSize = parseFloat(sizeInput.value);
        self._updateValueDisplay(sizeInput);
        self._render();
      });
    }

    // Leading slider
    var leadingInput = this.controlsEl.querySelector('[data-control="leading"]');
    if (leadingInput) {
      leadingInput.addEventListener('input', function () {
        self.state.leading = parseFloat(leadingInput.value);
        self._updateValueDisplay(leadingInput);
        self._render();
      });
    }

    // Tracking slider
    var trackingInput = this.controlsEl.querySelector('[data-control="tracking"]');
    if (trackingInput) {
      trackingInput.addEventListener('input', function () {
        self.state.tracking = parseFloat(trackingInput.value);
        self._updateValueDisplay(trackingInput);
        self._render();
      });
    }

    // Alignment buttons
    this.controlsEl.querySelectorAll('[data-align]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.controlsEl.querySelectorAll('[data-align]').forEach(function (b) {
          b.classList.remove('active');
          b.removeAttribute('aria-pressed');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        self.state.alignment = btn.dataset.align;
        self._render();
      });
    });

    // Feature toggle buttons
    this.controlsEl.querySelectorAll('[data-feature]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tag = btn.dataset.feature;
        self.state.features[tag] = !self.state.features[tag];
        btn.classList.toggle('active', self.state.features[tag]);
        btn.setAttribute('aria-pressed', self.state.features[tag]);
        self._render();
      });
    });

    // ALL CAPS / lowercase toggle
    var caseBtn = this.controlsEl.querySelector('[data-case-toggle]');
    if (caseBtn) {
      caseBtn.addEventListener('click', function () {
        self.state.uppercase = !self.state.uppercase;
        caseBtn.classList.toggle('active', self.state.uppercase);
        caseBtn.setAttribute('aria-pressed', String(self.state.uppercase));
        self._render();
      });
    }
  };

  TypeTester.prototype._updateValueDisplay = function (input) {
    var group = input.closest('[data-axis-group]');
    if (!group) return;
    var display = group.querySelector('[data-axis-value]');
    if (!display) return;

    var val = parseFloat(input.value);
    // Round whole numbers, show 2 decimals for small ranges
    var step = parseFloat(input.step || 1);
    display.textContent = step >= 1 ? Math.round(val) : val.toFixed(2);
  };

  TypeTester.prototype._render = function () {
    var state = this.state;
    var fontName = this.fontName;

    // Build font-variation-settings from ALL axes in config
    var axisKeys = Object.keys(this.config.defaultAxes);
    var fvs = axisKeys.map(function (axis) {
      var val = state.axes[axis];
      return '"' + axis + '" ' + (val !== undefined ? val : 0);
    }).join(', ');

    // Build font-feature-settings
    var ffsEntries = Object.entries(state.features);
    var ffs = ffsEntries.length
      ? ffsEntries.map(function (e) { return '"' + e[0] + '" ' + (e[1] ? 1 : 0); }).join(', ')
      : 'normal';

    Object.assign(this.textarea.style, {
      fontFamily: "'" + fontName + "', sans-serif",
      fontVariationSettings: fvs,
      fontFeatureSettings: ffs,
      fontSize: state.fontSize + 'px',
      lineHeight: state.leading,
      letterSpacing: state.tracking + 'em',
      textAlign: state.alignment,
      textTransform: state.uppercase ? 'uppercase' : ''
    });
  };

  // Polyfill Object.entries for older browsers
  if (!Object.entries) {
    Object.entries = function (obj) {
      return Object.keys(obj).map(function (k) { return [k, obj[k]]; });
    };
  }

  // Auto-init all type testers on the page
  document.querySelectorAll('[data-type-tester]').forEach(function (el) {
    new TypeTester(el);
  });

})();
